import React from 'react';

import { DialogTitle, Modal, ModalClose, ModalDialog, Stack, Typography } from '@mui/joy';

type Props = {
  open: boolean;
  onClose: () => void;
};

const Terms: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        aria-labelledby='terms-of-use-modal'
        sx={{
          maxWidth: 800,
          maxHeight: '90vh',
        }}
      >
        <ModalClose />
        <DialogTitle>Calyx Global Terms of Use ("ToU")</DialogTitle>
        <Stack sx={{ gap: 1, overflowY: 'auto' }}>
          <Typography level='body-sm' sx={{ fontStyle: 'italic' }}>
            With effect from 1st May 2025
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Application
          </Typography>
          <Typography level='body-md'>For the purposes of these ToU:</Typography>
          <Typography level='body-md'>
            <strong>"You"</strong> and its cognates mean any person accessing the Calyx Global app (
            <a
              href='https://app.calyxglobal.com/'
              target='_blank'
              style={{ textDecoration: 'underline' }}
            >
              https://app.calyxglobal.com/
            </a>
            ) who is not a customer, or designated user of such customer, under the terms of a
            subscription agreement with Calyx Global Inc.; and
          </Typography>
          <Typography level='body-md'>
            <strong>"Company"</strong> means Calyx Global Inc, whose business address is 518
            HARRISON AVENUE #1005, LEADVILLE, CO 80461, COLORADO, USA.
          </Typography>
          <Typography level='body-md'>
            The Company email address is:{' '}
            <a
              href='mailto:customersuccess@calyxglobal.com'
              style={{ textDecoration: 'underline' }}
            >
              customersuccess@calyxglobal.com
            </a>
          </Typography>
          <Typography level='body-md'>
            The Company telephone number is:{' '}
            <a href='tel:+16505300958' style={{ textDecoration: 'underline' }}>
              +1 (650) 530-0958
            </a>
          </Typography>
          <Typography level='body-md'>
            These ToU apply to any activity carried out by You on the Calyx Global app, including
            viewing, using, downloading, displaying or processing of data, information or material
            available on the app (<strong>"Calyx Global Data"</strong>). Your access to the app is
            governed by these ToU with which you agree to comply in consideration of Your being
            granted access. If You do not agree to these ToU, You are required to cease accessing or
            using the app.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            License terms
          </Typography>
          <Typography level='body-md'>
            Subject to these ToU, the Company grants to You a non-exclusive, non-sub-licensable and
            non-transferable license to access and use Calyx Global Data exclusively for the
            Permitted Purpose as defined below and subject to the Use Restrictions set out below.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Permitted purpose
          </Typography>
          <Typography level='body-md'>
            You may access and use Calyx Global Data for Your internal business purposes only.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Use restrictions
          </Typography>
          <Typography level='body-md'>You may not:</Typography>
          <ol
            style={{
              listStyleType: 'lower-roman',
              paddingLeft: '28px',
              margin: 0,
            }}
          >
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                "Scrape" the Calyx Global Data by downloading all Calyx Global Data, or all
                information pertaining to a specific type or class of project;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Copy, modify, or create derivative works of the Calyx Global Data, in whole or in
                part;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Rent, lease, lend, sell, sublicense, assign, distribute, publish, transfer, or
                otherwise make available the Calyx Global Data to a third party, or provide
                information about the Calyx Global Data to a third party which enables it to make
                inferences about Calyx Global Data;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Access or use the Calyx Global Data in order to build a competitive product or
                service or database or otherwise emulate the Company's ratings framework or
                approach;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Remove any proprietary notices included within the Calyx Global Data;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Use the Calyx Global Data in any manner or for any purpose that infringes,
                misappropriates, or otherwise violates any intellectual property right or other
                right of any person, or that violates any applicable law;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Use or disclose Calyx Global Data in connection with services to third parties
                including the selection or marketing of, or advice with respect to projects;
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Use or access, or allow any third party to use or access, Calyx Global Data as a
                resource or component of any artificial intelligence tools or services, including
                so-called generative artificial intelligence that can create text, images, video,
                audio, code, or other data based on training data; or
              </Typography>
            </li>
            <li style={{ marginBottom: '8px' }}>
              <Typography level='body-md'>
                Reverse engineer, decompile, disassemble, or otherwise attempt to derive the
                methodology, algorithms, data compilation techniques, or any other information
                related to the creation or compiling of Calyx Global Data.
              </Typography>
            </li>
          </ol>
          <Typography level='body-md'>
            For the purpose of this clause, third party includes an entity that is affiliated to or
            associated with You, or the officers, employees, contractors and agents of such entity.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Intellectual property
          </Typography>
          <Typography level='body-md'>
            You acknowledge that, as between You and the Company, the Company owns all rights, title
            and interest, including all the intellectual property rights with respect to the Calyx
            Global Data. Except for the limited rights and licenses expressly granted under these
            ToU, nothing in these ToU grants, by implication, waiver, estoppel, or otherwise, to You
            or any third party any intellectual property rights or other right, title, or interest
            in or to any Calyx Global Data. These materials are protected by copyright laws and
            treaties around the world. All such rights are reserved.
          </Typography>
          <Typography level='body-md'>
            You further acknowledge that: (a) the Calyx Global app, including any Calyx Global Data
            is an original compilation protected by intellectual property laws; and (b) the Company
            has dedicated substantial resources to collect, manage, and compile such data, and that
            such organization and compilation is proprietary to the Company.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Suitability of Calyx Global Data
          </Typography>
          <Typography level='body-md'>
            You acknowledge that the ratings and insights comprised in the Calyx Global Data are the
            result of applying the Company's proprietary frameworks to publicly available and/or
            acquirable information. Human judgment is involved in the application of the Company's
            frameworks; this may result in differing rating outcomes. You also accept that the
            quality and reliability of the assessment rating necessarily depend upon the quality and
            reliability of the data on which it is based and that the Company has limited
            opportunity to verify such data. The Company accepts no liability to You for any loss,
            expense, liability or claim that You may incur in connection with any rating assessment
            which is based upon erroneous data.
          </Typography>
          <Typography level='body-md'>
            The information provided in the form of Calyx Global Data does not constitute any form
            of advice or recommendation with respect to the relevant carbon project. A Company
            rating does not indicate that a particular carbon project is suitable for procurement or
            investment by You or any other person and does not provide any forecast whatsoever as to
            a carbon project's future performance. The Company does not accept any liability
            whatsoever for any loss, expense, cost or liability, howsoever arising from Your
            decision to procure credits from, participate in, invest in or in any manner take
            financial exposure with respect to any carbon project on which the Company has provided
            information.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Availability of Calyx Global app
          </Typography>
          <Typography level='body-md'>
            The Company shall use reasonable endeavors to ensure that the Calyx Global app is
            continuously accessible, but reserves the right to suspend availability during periods
            of website or app maintenance which the Company shall notify to users of the app with as
            much notice as reasonably possible. You acknowledge that accessibility or use of the app
            relies upon Internet resources over which neither party has control, and the Company
            does not guarantee access at all times for that reason. You are responsible for making
            all arrangements necessary for You to have access to the Calyx Global app. The Company
            takes reasonable steps to secure its data, software and hardware used to manage and
            transmit that data from cyber-attacks, malicious hacking and the introduction by any
            means of any unauthorized intrusive code or programming into data processing equipment,
            media, software, programs, systems or records. However, Company does not guarantee that
            Calyx Global Data provided is free of all such unauthorized intrusive code or
            programming and does not accept liability with respect to any loss, liability, expense
            or claim which You incur by reason of the contamination of such data by such
            unauthorized intrusive code or programming.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Updates
          </Typography>
          <Typography level='body-md'>
            The Company is under no obligation to update the content of any Calyx Global Data, but
            reserves the right to do so, in response to new information or data. The Company accepts
            no liability to You for any loss, liability, expense or claim incurred by You in
            connection with any variation between the Calyx Global Data provided to You and its
            updated version.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Your responsibilities
          </Typography>
          <Typography level='body-md'>
            You must not damage, interfere with or disrupt access to the Calyx Global app, or do
            anything which might interrupt or impair its functionality. You must not misuse the
            Calyx Global app by knowingly introducing viruses, trojans, worms, logic bombs or other
            material which is malicious or technologically harmful. You must not attempt to gain
            unauthorized access to the Calyx Global app, the server(s) or cloud service on which
            Calyx Global Data is stored or any server, computer or database connected to the Calyx
            Global app. You must not attack the Calyx Global app via a denial-of-service attack or a
            distributed denial-of service attack.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            LIMITATION OF LIABILITY
          </Typography>
          <Typography level='body-md' sx={{ textTransform: 'uppercase' }}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE LIABILITY OF THE COMPANY WITH
            RESPECT TO ANY AND ALL SUITS, ACTIONS, LEGAL PROCEEDINGS, CLAIMS, DEMANDS, DAMAGES,
            COSTS AND EXPENSES ARISING OUT OF YOUR ACTIVITY ON THE CALYX GLOBAL APP, INCLUDING
            VIEWING, USING, DOWNLOADING, DISPLAYING OR PROCESSING OF CALYX GLOBAL DATA, WHETHER
            BASED ON CONTRACT, WARRANTY, TORT (INCLUDING, WITHOUT LIMITATION, NEGLIGENCE), STRICT
            LIABILITY OR BREACH OF DUTY, STATUTORY OR OTHERWISE, IS HEREBY EXPRESSLY EXCLUDED. SUCH
            EXCLUSION OF LIABILITY SHALL EXTEND TO ANY INCIDENTAL, SPECIAL, CONSEQUENTIAL OR
            INDIRECT DAMAGES, DAMAGES RESULTING FROM LOSS OF SALE, BUSINESS, PROFITS, OPPORTUNITY OR
            GOODWILL, PROPERTY DAMAGE AND PERSONAL INJURY.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            DISCLAIMER OF WARRANTIES
          </Typography>
          <Typography level='body-md' sx={{ textTransform: 'uppercase' }}>
            ALL CALYX GLOBAL DATA ARE PROVIDED "AS IS" AND THE COMPANY HEREBY DISCLAIMS ALL
            WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE IN RESPECT THEREOF. THE
            COMPANY SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, AND ALL WARRANTIES ARISING FROM COURSE
            OF DEALING, USAGE, OR TRADE PRACTICE. THE COMPANY MAKES NO WARRANTY OF ANY KIND THAT
            CALYX GLOBAL APP, CALYX GLOBAL DATA, OR ANY PRODUCTS, DATA OR RESULTS OF ITS USE, WILL
            MEET YOUR OR ANY OTHER PERSON'S REQUIREMENTS, OPERATE WITHOUT INTERRUPTION, ACHIEVE ANY
            INTENDED RESULT, BE COMPATIBLE OR WORK WITH ANY SOFTWARE, SYSTEM, OR OTHER SERVICES, OR
            BE SECURE, ACCURATE, COMPLETE, FREE OF HARMFUL CODE, OR ERROR FREE. ANY ACTION THAT YOU
            TAKE BASED ON THE SERVICES OR PRODUCTS, DATA OR RESULTS THEREFROM IS SOLELY YOUR
            RESPONSIBILITY, AND THE COMPANY SHALL NOT BE RESPONSIBLE OR LIABLE FOR SUCH ANY ACTIONS.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Personal data
          </Typography>
          <Typography level='body-md'>
            The Company retains and processes Your personal data, but only to the extent strictly
            necessary to facilitate communications with You, in accordance with the Company's
            Privacy Policy available at this webpage{' '}
            <a
              href='https://calyxglobal.com/privacy-policy'
              target='_blank'
              style={{ textDecoration: 'underline' }}
            >
              https://calyxglobal.com/privacy-policy
            </a>
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Governing law
          </Typography>
          <Typography level='body-md'>
            The Agreement is governed by the laws of the State of New York save for any conflict of
            laws provisions which would otherwise provide for a different law to apply.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Jurisdiction
          </Typography>
          <Typography level='body-md'>
            As a condition of Your access to the Calyx Global app and/or Calyx Global Data, You
            agree to submit to the personal and exclusive jurisdiction of the federal and state
            courts located within New York.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Denial of access
          </Typography>
          <Typography level='body-md'>
            The Company reserves the right to deny You access to the Calyx Global app and/or Calyx
            Global Data in the event that the Company reasonably forms the opinion that You are in
            breach of the ToU, and that You have failed to respond to the Company's reasonable
            efforts to contact You and to require You to remedy such breach.
          </Typography>

          <Typography level='h3' component='h2' sx={{ mt: 3, mb: 1 }}>
            Other terms
          </Typography>
          <Typography level='body-md'>
            If any provision of these Terms of Use is found invalid or unenforceable, that provision
            shall be enforced to the maximum extent possible, and the other provisions contained
            herein will remain in full force and effect. Calyx Global's failure to insist upon or
            enforce strict performance of any provision of the Terms of Use shall not be construed
            as a waiver of any provision or right. These Terms of Use constitute the entire
            agreement between You and Calyx Global with respect to Your access to the Calyx Global
            app and/or Calyx Global Data. These Terms of Use will be deemed to be enforceable as a
            signed writing as against the parties, and deemed an "original" and "in writing" when
            printed from electronic records established and maintained in the ordinary course of
            business. You may not assign your rights and obligations under these Terms of Use, in
            whole or in part, without Calyx Global's prior written consent, and any such assignment
            without such consent will be null and void. Calyx Global may assign its rights and
            obligations under these Terms of Use, in whole or in part, without Your consent. These
            Terms of Use will inure to the benefit and burden of the parties hereto and their
            permitted successors and assigns.
          </Typography>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export { Terms };
